<template>
    <app-layout>
        <template v-slot:header>
            <PageHeader
                :title="$t('vi_title')"
                :isNewButton="checkPermission('virtualinternshipprogram_store')"
                @new-button-click="createFormShow()"
                :otherButton="{
                    show: checkPermission('virtualinternshipprogram_bulkstore'),
                    title: $t('vi_bulk_add'),
                    icon: 'ri-user-add-line',
                    emit: 'bulk-create-button-click'
                }"
                @bulk-create-button-click="createFormShow('bulk')"
                @filter-div-status="datatable.filterStatus = $event"
            />
        </template>
        <template v-slot:header-mobile>
            <HeaderMobile
                :title="$t('vi_title')"
                :isNewButton="checkPermission('virtualinternshipprogram_store')"
                @new-button-click="createFormShow()"
                :actions="actions"
                @filter-div-status="datatable.filterStatus = $event"
            />
        </template>
        <div class="container-fluid py-3">
            <datatable-filter
                v-show="datatable.filterStatus"
                :export-excel="checkPermission('virtualinternshipprogram_excelexport')"
                @filter="filter"
                @filterClear="filterClear"
                @exportExcel="exportExcel"
            >
                <b-row>
                    <b-col md="4" sm="6">
                        <b-form-group :label="$t('semester')">
                            <semesters-selectbox
                                v-model="datatable.queryParams.filter.semester_id"
                                :multiple="false"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="4" sm="6">
                        <b-form-group :label="$t('student_number')">
                            <b-form-input v-model="datatable.queryParams.filter.student_number" />
                        </b-form-group>
                    </b-col>
                    <b-col md="4" sm="6">
                        <b-form-group :label="$t('name')">
                            <b-form-input v-model="datatable.queryParams.filter.name" />
                        </b-form-group>
                    </b-col>
                    <b-col md="4" sm="6">
                        <b-form-group :label="$t('surname')">
                            <b-form-input v-model="datatable.queryParams.filter.surname" />
                        </b-form-group>
                    </b-col>
                    <b-col md="4" sm="6">
                        <b-form-group :label="$t('program')">
                            <program-selectbox
                                v-model="datatable.queryParams.filter.program_code"
                                :multiple="true"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="4" sm="6">
                        <b-form-group :label="$t('registration_type')">
                            <parameter-selectbox
                                v-model="datatable.queryParams.filter.registration_type"
                                code="registration_types"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="4" sm="6">
                        <b-form-group :label="$t('vi_course_registration_completed')">
                            <b-form-select
                                v-model="datatable.queryParams.filter.course_registration_completed"
                                :options="filterYesNoOptions"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="4" sm="6">
                        <b-form-group :label="$t('vi_application_submitted')">
                            <yes-no-selectbox
                                v-model="datatable.queryParams.filter.vi_application_submitted"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="4" sm="6">
                        <b-form-group :label="$t('grade')">
                            <grade-selectbox
                                v-model="datatable.queryParams.filter.grade"
                                :grading-system-id-required="false"
                                value-type="grade"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>
            <datatable
                :showTable="datatable.showTable"
                :isLoading.sync="datatable.isLoading"
                :columns="datatable.columns"
                :rows="datatable.rows"
                :total="datatable.total"
                :queryParams="datatable.queryParams"
                :lineNumbers="false"
                @on-page-change="onPageChange"
                @on-per-page-change="onPerPageChange"
            />
        </div>
        <CommonModal
            ref="editor"
            id="virtual-internships-program-modal"
            size="md"
            :noCloseOnBackDrop="formLoading"
            :closeBtn="!formLoading"
            @onHidden="formClear"
        >
            <template v-slot:CommonModalTitle>
                <h5 class="modal-title m-0">
                    {{ $t(formProcess === 'update' ? 'edit' : formProcess === 'bulk' ? 'vi_bulk_add' : 'new') }}
                </h5>
            </template>
            <template v-slot:CommonModalContent>
                <program-form
                    v-if="formProcess"
                    :key="formProcess + '-' + formId"
                    :formId="formId"
                    :formProcess="formProcess"
                    :semesterId="datatable.queryParams.filter.semester_id || null"
                    :selectedStudent="selectedStudent"
                    @createFormSuccess="createFormSuccess"
                    @updateFormSuccess="updateFormSuccess"
                    @formLoading="formLoading = $event"
                    @cancel="$refs.editor.$refs.commonModal.hide()"
                />
            </template>
        </CommonModal>
    </app-layout>
</template>

<script>
import AppLayout from '@/layouts/AppLayout';
import PageHeader from '@/layouts/AppLayout/Header';
import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';
import Datatable from '@/components/datatable/Datatable';
import DatatableFilter from '@/components/datatable/DatatableFilter';
import GradeSelectbox from '@/components/interactive-fields/GradeSelectbox';
import YesNoSelectbox from '@/components/interactive-fields/YesNoSelectbox';
import VirtualInternshipsProgramService from '@/services/VirtualInternshipsProgramService';
import qs from 'qs';
import CommonModal from '@/components/elements/CommonModal';
import ProgramForm from './Form';

export default {
    name: 'VirtualInternshipsProgram',
    components: {
        AppLayout,
        PageHeader,
        HeaderMobile,
        Datatable,
        DatatableFilter,
        GradeSelectbox,
        YesNoSelectbox,
        CommonModal,
        ProgramForm
    },
    metaInfo() {
        return { title: this.$t('vi_title') };
    },
    data() {
        return {
            actions: [
                {
                    text: this.$t('vi_bulk_add'),
                    permission: 'virtualinternshipprogram_bulkstore',
                    callback: () => this.createFormShow('bulk')
                }
            ],
            formId: null,
            formProcess: null,
            selectedStudent: '',
            formLoading: false,
            datatable: {
                columns: [
                    {
                        label: '',
                        field: 'buttons',
                        html: true,
                        sortable: false,
                        tdClass: 'p-0 text-center w-40 align-middle',
                        buttons: [
                            {
                                text: this.$t('edit'),
                                class: 'ri-edit-line',
                                permission: 'virtualinternshipprogram_update',
                                callback: (row) => this.updateFormShow(row)
                            },
                            {
                                text: this.$t('delete'),
                                class: 'ri-delete-bin-line',
                                permission: 'virtualinternshipprogram_delete',
                                callback: (row) => this.deleteForm(row.id)
                            }
                        ]
                    },
                    {
                        label: this.$t('student_number'),
                        field: 'student_id',
                        sortable: false
                    },
                    {
                        label: this.$t('name'),
                        field: 'first_name',
                        sortable: false
                    },
                    {
                        label: this.$t('surname'),
                        field: 'last_name',
                        sortable: false
                    },
                    {
                        label: this.$t('program'),
                        field: 'program',
                        sortable: false
                    },
                    {
                        label: this.$t('registration_type'),
                        field: 'registration_type_name',
                        sortable: false
                    },
                    {
                        label: this.$t('vi_application_submitted'),
                        field: 'vi_application_submitted',
                        sortable: false
                    },
                    {
                        label: this.$t('vi_course_registration_completed'),
                        field: 'course_registration_completed',
                        sortable: false
                    },
                    {
                        label: this.$t('vi_first_installment_amount'),
                        field: 'vi_fee_first_installment_amount',
                        sortable: false
                    },
                    {
                        label: this.$t('vi_first_installment_purchased_at'),
                        field: 'vi_fee_first_installment_purchase_date',
                        sortable: false
                    },
                    {
                        label: this.$t('vi_second_installment_amount'),
                        field: 'vi_fee_second_installment_amount',
                        sortable: false
                    },
                    {
                        label: this.$t('vi_second_installment_purchased_at'),
                        field: 'vi_fee_second_installment_purchase_date',
                        sortable: false
                    },
                    {
                        label: this.$t('grade'),
                        field: 'grade',
                        sortable: false
                    }
                ],
                filterStatus: true,
                showTable: false,
                isLoading: false,
                rows: [],
                total: 0,
                queryParams: {
                    filter: {},
                    sort: '-id',
                    page: 1,
                    limit: 20
                }
            }
        };
    },
    computed: {
        filterYesNoOptions() {
            return [
                { value: null, text: this.$t('select') },
                { value: 'Yes', text: this.$t('yes') },
                { value: 'No', text: this.$t('no') }
            ];
        }
    },
    created() {
        this.getRows();
    },
    methods: {
        filter() {
            this.datatable.queryParams.page = 1;
            this.getRows();
        },
        filterClear() {
            this.datatable.queryParams.filter = {};
            this.filter();
        },
        onPageChange(params) {
            this.datatable.queryParams.page = params.currentPage;
            this.getRows();
        },
        onPerPageChange(params) {
            this.datatable.queryParams.limit = params.currentPerPage;
            this.datatable.queryParams.page = 1;
            this.getRows();
        },
        getRows() {
            this.datatable.showTable = true;
            this.datatable.isLoading = true;

            const config = {
                params: {
                    ...this.datatable.queryParams
                },
                paramsSerializer: (params) => qs.stringify(params, {encode: false})
            };

            return VirtualInternshipsProgramService.getAll(config)
                .then((response) => {
                    this.datatable.rows = response.data.data;
                    this.datatable.total = response.data.pagination.total;
                })
                .finally(() => {
                    this.datatable.isLoading = false;
                });
        },
        exportExcel() {
            const config = {
                params: {
                    filter: this.datatable.queryParams.filter,
                    sort: this.datatable.queryParams.sort
                },
                paramsSerializer: (params) => qs.stringify(params, {encode: false})
            };

            return VirtualInternshipsProgramService.excelExport(config)
                .then((response) => {
                    this._downloadFile(response, 'Virtual-Internships-Program.xlsx');
                })
                .catch((error) => this.showErrors(error, { errors: {} }, true));
        },
        createFormShow(process = 'create') {
            this.formId = null;
            this.selectedStudent = '';
            this.formProcess = process;
            this.$refs.editor.$refs.commonModal.show();
        },
        updateFormShow(row) {
            this.formId = row.id;
            this.selectedStudent = `${row.student_id} - ${row.first_name} ${row.last_name}`;
            this.formProcess = 'update';
            this.$refs.editor.$refs.commonModal.show();
        },
        createFormSuccess() {
            this.$refs.editor.$refs.commonModal.hide();
            this.filter();
        },
        updateFormSuccess() {
            this.$refs.editor.$refs.commonModal.hide();
            this.getRows();
        },
        formClear() {
            this.formId = null;
            this.formProcess = null;
            this.selectedStudent = '';
            this.formLoading = false;
        },
        deleteForm(id) {
            this.$swal.fire({
                text: this.$t('are_you_sure_to_delete'),
                showCancelButton: true,
                confirmButtonText: this.$t('yes'),
                cancelButtonText: this.$t('no')
            }).then((result) => {
                if (result.isConfirmed) {
                    VirtualInternshipsProgramService.deleteProgram(id)
                        .then((response) => {
                            this.$toast.success(this.$t('api.' + response.data.message))
                            this.getRows();
                        })
                        .catch((error) => this.showErrors(error, { errors: {} }));
                }
            });
        }
    }
};
</script>
